// import atoms
import {
  canDownloadReportAtom,
  dateRangeAtom,
  selectedChipAtom
} from '../atoms';

// import components
import Button from '@sdb/ui/atoms/Button';
import { Box, LinearProgress, Typography } from '@mui/material';
import { StyledTile } from 'components/shared/Tile/Tile.styles';

// import external libraries
import { addDays, isAfter, isValid, startOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import EventBus from 'lib/EventBus';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';

// import hooks
import {
  useDownloadRealizationReport,
  useRecalculateDutyLinesMutation
} from '../queries';

// import types
import { DutyLinesProgress } from '../types';

// import utils
import { formatByLocale } from 'utils/date';

const eventBusUrl = '/calculation/dutylines';

const RecalculateDuties = () => {
  const { t } = useTranslation();
  const [dateRange] = useRecoilState(dateRangeAtom);
  const [progress, setProgress] = useState<DutyLinesProgress | null>(null);

  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const canDownloadReport = useRecoilValue(canDownloadReportAtom);
  const selectedChip = useRecoilValue(selectedChipAtom);

  const { mutate: calculateDutyLines, isError } =
    useRecalculateDutyLinesMutation();
  const { refetch, isLoading } = useDownloadRealizationReport({
    validFrom: dateRange[0],
    validTo: startOfDay(addDays(dateRange[1] as Date, 1))
  });

  useEffect(() => {
    if (selectedChip || isError) {
      setProgress(null);
      setIsInProgress(false);
      EventBus.unregister(eventBusUrl, onRecalculateProgress);
    }
  }, [selectedChip, isError]);

  const handleDownloadClick = () => {
    refetch();
  };

  const onRecalculateProgress = (newProgress: DutyLinesProgress) => {
    if (newProgress.hasFinished) {
      setIsInProgress(false);
      setProgress(newProgress);
      EventBus.unregister(eventBusUrl, onRecalculateProgress);
    } else {
      setProgress(newProgress);
    }
  };

  const onClickRecalculate = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }

    setIsInProgress(true);
    EventBus.register(eventBusUrl, onRecalculateProgress);

    await calculateDutyLines({
      startDate: dateRange[0] as Date,
      endDate: startOfDay(addDays(dateRange[1] as Date, 1))
    });
  };

  const isValidDateRange =
    dateRange[0] &&
    dateRange[1] &&
    isValid(dateRange[0]) &&
    isValid(dateRange[1]);

  const dateRangeMisMatchError = Boolean(
    dateRange[0] &&
      dateRange[1] &&
      isAfter(dateRange[0] as Date, dateRange[1] as Date)
  );

  return (
    <StyledTile
      title={t('adminPortal.realization.recalculateDuties.name')}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        opacity: isValidDateRange && !dateRangeMisMatchError ? 1 : 0.5
      }}
      data-testid="recalculate-duties"
    >
      <Typography variant="body2" sx={{ fontFamily: 'SourceSansPro-Regular' }}>
        {t('adminPortal.realization.recalculateDuties.description')}
      </Typography>
      <Typography
        variant="body2"
        sx={{ paddingTop: 2, fontFamily: 'SourceSansPro-Regular' }}
      >
        {t('adminPortal.realization.recalculateDuties.warning')}
      </Typography>
      <Box pt={3}>
        {(!progress || progress.hasFinished) && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              size="small"
              variant="tonal"
              disabled={
                isInProgress || !isValidDateRange || dateRangeMisMatchError
              }
              onClick={onClickRecalculate}
            >
              {t('adminPortal.realization.calculate')}
            </Button>
            {!dateRangeMisMatchError && isValidDateRange && (
              <Typography>
                {`${formatByLocale(dateRange[0]! as Date)} ${t(
                  'until'
                )} ${formatByLocale(dateRange[1]! as Date)}`}
              </Typography>
            )}
          </Box>
        )}
        {progress && !progress.hasFinished && (
          <LinearProgress
            variant="determinate"
            value={(progress.dutyLinesProgress / progress.dutyLinesTotal) * 100}
          />
        )}
        {progress && (
          <Typography sx={{ mt: '16px!important' }}>
            {progress.dutyLinesProgress}/{progress.dutyLinesTotal},{' '}
            {progress.dutyLinesErrors} {t('errors')}
          </Typography>
        )}
        {progress?.hasFinished &&
          progress?.dutyLinesErrors > 0 &&
          canDownloadReport && (
            <Box mt={2}>
              <Button
                size="small"
                variant="tonal"
                disabled={isLoading}
                onClick={handleDownloadClick}
              >
                {t('downloadExcelReport')}
              </Button>
            </Box>
          )}
      </Box>
    </StyledTile>
  );
};

export default RecalculateDuties;
